import { GrowthBookProvider as GrowthBProvider, GrowthBook } from '@growthbook/growthbook-react';
import { noop } from 'lodash';
import React, { ReactNode, useEffect } from 'react';
import store from 'store2';

import { captureException } from '@skippay/core/global/errorTracking';

const FEATURES_CACHE_KEY = 'growthbook_features';
const FEATURES_CACHE_MAX_AGE = 48 * 60 * 60 * 1000; // 2 days

const growthbook = new GrowthBook({
	apiHost: 'https://cdn.growthbook.io',
	clientKey: process.env.GROWTH_BOOK,
	enableDevMode:
		process.env.NODE_ENV === 'development' ||
		process.env.TARGET_ENV === 'accept' ||
		process.env.TARGET_ENV === 'nightly',
	attributes: {
		environment: process.env.TARGET_ENV === 'prod' ? 'production' : 'accept',
	},
	trackingCallback: (experiment, result) => {
		if ('dataLayer' in window) {
			window.dataLayer.push({
				event: 'experiment_viewed',
				content_name: result.name,
				content_id: `${experiment.key}:${result.variationId}`,
			});
		}
	},
});

function getGaCookieValue(): string | undefined {
	return document.cookie.match(/_ga=(.+?)(;|$)/)?.[1];
}

function waitForGaCookie(callback: (value: string) => void): () => void {
	const immediate = getGaCookieValue();
	if (immediate) {
		callback(immediate);
		return noop;
	}

	let settled = false;

	const dataLayer = window.dataLayer || [];
	const originalPush = dataLayer.push.bind(dataLayer);
	dataLayer.push = (...args) => {
		const result = originalPush(...args);
		if (!settled) {
			const value = getGaCookieValue();
			if (value) {
				settled = true;
				dataLayer.push = originalPush;
				callback(value);
			}
		}
		return result;
	};

	const fallbackTimer = setTimeout(() => {
		if (!settled) {
			settled = true;
			dataLayer.push = originalPush;
			const value = getGaCookieValue();
			if (value) {
				callback(value);
			}
		}
	}, 3000);

	return () => {
		settled = true;
		dataLayer.push = originalPush;
		clearTimeout(fallbackTimer);
	};
}

function loadCachedFeatures() {
	const cachedFeatures = store.local.get(FEATURES_CACHE_KEY);

	if (cachedFeatures) {
		try {
			const { features, timestamp } = cachedFeatures;
			const now = Date.now();
			if (features && timestamp && now - timestamp < FEATURES_CACHE_MAX_AGE) {
				growthbook.setFeatures(features);
			} else {
				store.local.remove(FEATURES_CACHE_KEY);
			}
		} catch {
			// ignore cache errors (e.g. invalid JSON)
		}
	}
}

function setup(userId: string) {
	growthbook
		.loadFeatures({
			timeout: 2000,
		})
		.then(() => {
			store.local.set(FEATURES_CACHE_KEY, {
				features: growthbook.getFeatures(),
				timestamp: Date.now(),
			});
		})
		.catch((originalError) =>
			captureException(new Error('Failed to load AB test features'), {
				extra: { originalError },
			})
		);
	growthbook.setAttributes({
		id: userId,
		viewportWidth: window.innerWidth,
	});
}

function GrowthBookProvider({ children }: { children?: ReactNode | undefined }): JSX.Element {
	useEffect(() => {
		loadCachedFeatures();

		if (process.env.NODE_ENV === 'development') {
			// Use a static user ID in development so we can see the same variation
			setup('00001');
			return;
		}

		// Load features asynchronously when GA cookie becomes available

		// eslint-disable-next-line consistent-return
		return waitForGaCookie((value) => {
			const gaUserId = value.split('.').slice(-2).join('.');

			if (!gaUserId) {
				return;
			}

			setup(gaUserId);
		});
	}, []);

	return <GrowthBProvider growthbook={growthbook}>{children}</GrowthBProvider>;
}

export default GrowthBookProvider;
